'use client';

import { c as _c } from "react/compiler-runtime";
import { AppSearchBar } from '@/components/ui/app-search-bar';
import { Card } from '@/components/ui/card';
import { FactRow } from '@/components/ui/fact-row';
import { SectionLabel } from '@/components/ui/section-label';
import { findVisitDayHoefe } from '@/features/visit-day/find-visit-day-hof';
import { formatVisitDay } from '@/features/visit-day/format-visit-day';
import { useCurrentLocale } from '@/hooks/use-current-locale';
import { trpc } from '@/trpc/client';
import type { Locale, StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { CalendarDays } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

/** More matches than this mean the search is too short to be an answer. */
const MAX_MATCHES = 6;
const placeholderText: StaticTranslationString = {
  de: 'Abteilung suchen, z.B. Uster',
  en: 'Search a local group, e.g. Uster',
  fr: 'Chercher un groupe local, p. ex. Uster'
};
const searchLabel: StaticTranslationString = {
  de: 'Abteilung der teilnehmenden Person suchen',
  en: 'Search the local group of the participant',
  fr: 'Chercher le groupe local de la personne participante'
};
const loadingText: StaticTranslationString = {
  de: 'Abteilungen werden geladen …',
  en: 'Loading local groups …',
  fr: 'Chargement des groupes locaux …'
};
const loadErrorText: StaticTranslationString = {
  de: 'Die Abteilungen konnten nicht geladen werden. Prüfe deine Verbindung.',
  en: 'The local groups could not be loaded. Check your connection.',
  fr: 'Les groupes locaux n’ont pas pu être chargés. Vérifie ta connexion.'
};

// the page says right below what a visitor without a Hof does, so this does not repeat it
const noMatchText: StaticTranslationString = {
  de: 'Keine Abteilung gefunden. Prüfe die Schreibweise.',
  en: 'No local group found. Check the spelling.',
  fr: 'Aucun groupe local trouvé. Vérifie l’orthographe.'
};
const moreMatchesText: StaticTranslationString = {
  de: 'Weitere Abteilungen passen, gib mehr vom Namen ein.',
  en: 'More local groups match, type more of the name.',
  fr: 'D’autres groupes locaux correspondent, tape davantage du nom.'
};

/**
 * Relatives type the Abteilung of the child they visit and see the day they come. They search
 * the Höfe, which are named after their Abteilungen, and the day follows from the Hof's
 * Quartier, neither of which they need to know. Reads the same list as the registration form.
 */
export const VisitDayFinder: React.FC<{
  title?: string | null | undefined;
}> = t0 => {
  const $ = _c(35);
  const {
    title
  } = t0;
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchOnMount: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const hoefe = trpc.visitDay.getVisitDayHofList.useQuery(undefined, t1);
  let t2;
  if ($[1] !== hoefe.data) {
    t2 = hoefe.data ?? [];
    $[1] = hoefe.data;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let result;
  if ($[3] !== hoefe.data || $[4] !== hoefe.fetchStatus || $[5] !== hoefe.isError || $[6] !== locale || $[7] !== search || $[8] !== t2) {
    const matches = findVisitDayHoefe(t2, search);
    const unavailable = hoefe.data === undefined && (hoefe.isError || hoefe.fetchStatus === "paused");
    if (search.trim() === "") {
      result = undefined;
    } else {
      if (unavailable) {
        const t3 = loadErrorText[locale];
        let t4;
        if ($[10] !== t3) {
          t4 = <p className="m-0 text-[15px] text-green-500">{t3}</p>;
          $[10] = t3;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        result = t4;
      } else {
        if (hoefe.data === undefined) {
          const t3 = loadingText[locale];
          let t4;
          if ($[12] !== t3) {
            t4 = <p className="m-0 text-[15px] text-green-500">{t3}</p>;
            $[12] = t3;
            $[13] = t4;
          } else {
            t4 = $[13];
          }
          result = t4;
        } else {
          if (matches.length === 0) {
            const t3 = noMatchText[locale];
            let t4;
            if ($[14] !== t3) {
              t4 = <p className="m-0 text-[15px] text-green-500">{t3}</p>;
              $[14] = t3;
              $[15] = t4;
            } else {
              t4 = $[15];
            }
            result = t4;
          } else {
            let t3;
            if ($[16] !== locale) {
              t3 = hof => <FactRow key={hof.id} icon={CalendarDays} title={formatVisitDay(hof.date, locale)} sub={hof.name} />;
              $[16] = locale;
              $[17] = t3;
            } else {
              t3 = $[17];
            }
            const t4 = <Card>{matches.slice(0, MAX_MATCHES).map(t3)}</Card>;
            const t5 = matches.length > MAX_MATCHES && <p className="mt-2 mb-0 text-[13px] text-green-500">{moreMatchesText[locale]}</p>;
            let t6;
            if ($[18] !== t4 || $[19] !== t5) {
              t6 = <>{t4}{t5}</>;
              $[18] = t4;
              $[19] = t5;
              $[20] = t6;
            } else {
              t6 = $[20];
            }
            result = t6;
          }
        }
      }
    }
    $[3] = hoefe.data;
    $[4] = hoefe.fetchStatus;
    $[5] = hoefe.isError;
    $[6] = locale;
    $[7] = search;
    $[8] = t2;
    $[9] = result;
  } else {
    result = $[9];
  }
  let t3;
  if ($[21] !== title) {
    t3 = typeof title === "string" && title !== "" && <SectionLabel className="mt-0 mb-2">{title}</SectionLabel>;
    $[21] = title;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const t4 = placeholderText[locale];
  const t5 = searchLabel[locale];
  let t6;
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => setSearch(event.target.value);
    t7 = () => setSearch("");
    $[23] = t6;
    $[24] = t7;
  } else {
    t6 = $[23];
    t7 = $[24];
  }
  let t8;
  if ($[25] !== search || $[26] !== t4 || $[27] !== t5) {
    t8 = <AppSearchBar onPage={true} placeholder={t4} label={t5} value={search} onChange={t6} onClear={t7} />;
    $[25] = search;
    $[26] = t4;
    $[27] = t5;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== result) {
    t9 = <div aria-live="polite" className="mt-2 empty:mt-0">{result}</div>;
    $[29] = result;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t3 || $[32] !== t8 || $[33] !== t9) {
    t10 = <div>{t3}{t8}{t9}</div>;
    $[31] = t3;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
};